const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".avif"];

function normalizeText(value) {
  return String(value || "").trim();
}

function compactText(value) {
  return normalizeText(value).replace(/\s+/g, "");
}

function dashText(value) {
  return normalizeText(value).replace(/\s+/g, "-");
}

function underscoreText(value) {
  return normalizeText(value).replace(/\s+/g, "_");
}

function lower(value) {
  return normalizeText(value).toLowerCase();
}

function normalizeLookupKey(value) {
  return lower(value)
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function textVariants(value) {
  const raw = normalizeText(value);
  const normalized = normalizeLookupKey(value);
  return Array.from(
    new Set([
      raw,
      lower(raw),
      compactText(raw),
      dashText(raw),
      underscoreText(raw),
      normalized,
      compactText(normalized),
      dashText(normalized),
      underscoreText(normalized),
    ].filter(Boolean))
  );
}

export function getVehicleTypeBucket(type) {
  const t = lower(type);

  if (!t) return "suv";
  if (t.includes("hatch")) return "hatchback";
  if (t.includes("sedan")) return "sedan";
  if (t.includes("suv") || t.includes("muv") || t.includes("mpv") || t.includes("4x4")) {
    return "suv";
  }
  return "suv";
}

export function getVehicleSkeletonPath(type) {
  const bucket = getVehicleTypeBucket(type);

  if (bucket === "hatchback") return "/CarDetailsPhoto/Hatchback_Skeleton.png";
  if (bucket === "sedan") return "/CarDetailsPhoto/Sedan_Skeleton.jpg";
  return "/CarDetailsPhoto/SUV_Skeleton.jpg";
}

function stripExtension(path) {
  const raw = normalizeText(path);
  if (!raw) return "";
  return raw.replace(/\.(png|jpg|jpeg|webp|avif)$/i, "");
}

function buildPathVariantsFromKnownPath(path) {
  const raw = normalizeText(path);
  if (!raw) return [];

  const noQuery = raw.split("?")[0].split("#")[0];
  const base = stripExtension(noQuery);

  if (!base) return [];

  const variants = [noQuery];

  IMAGE_EXTENSIONS.forEach((ext) => {
    variants.push(`${base}${ext}`);
  });

  const fileName = base.split("/").pop() || "";
  const parent = base.slice(0, base.length - fileName.length);

  const fileVariants = new Set([
    ...textVariants(fileName),
    ...textVariants(fileName.replace(/%20/g, " ")),
  ]);

  Array.from(fileVariants).forEach((name) => {
    IMAGE_EXTENSIONS.forEach((ext) => {
      variants.push(`${parent}${name}${ext}`);
    });
  });

  return variants;
}

function buildModelColorCandidates(model, color) {
  const modelNames = textVariants(model);
  const colorNames = textVariants(color);

  if (!modelNames.length || !colorNames.length) return [];

  const names = new Set();

  modelNames.forEach((m) => {
    colorNames.forEach((c) => {
      names.add(`${m}_${c}`);
      names.add(`${m}-${c}`);
      names.add(`${m}${c}`);
    });
  });

  const paths = [];
  Array.from(names).forEach((name) => {
    IMAGE_EXTENSIONS.forEach((ext) => {
      paths.push(`/CarDetailsPhoto/${name}${ext}`);
    });
  });

  return paths;
}

function getDirectServiceImage(type, brand, model) {
  const value = lower(type || brand || model);

  if (!value) return "";
  if (value.includes("rickshaw") || value.includes("rikshaw") || value.includes("erickshaw")) {
    return "/CarDetailsPhoto/E_Rikshaw.png";
  }
  if (value.includes("auto")) {
    return "/CarDetailsPhoto/Auto.png";
  }
  return "";
}

function dedupe(items) {
  const out = [];
  const seen = new Set();

  items.forEach((item) => {
    const key = normalizeText(item);
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(key);
  });

  return out;
}

export function buildVehicleImageCandidates({
  imageUrl,
  model,
  color,
  type,
  brand,
  modelImage,
  allowSkeleton = true,
}) {
  const candidates = dedupe([
    ...buildPathVariantsFromKnownPath(imageUrl),
    ...buildPathVariantsFromKnownPath(modelImage),
    ...buildModelColorCandidates(model, color),
    getDirectServiceImage(type, brand, model),
    allowSkeleton ? getVehicleSkeletonPath(type) : "",
  ]);

  return candidates;
}

export function buildVehicleImageIndex(details) {
  const index = Object.create(null);

  Object.keys(details || {}).forEach((brand) => {
    const brandNode = details?.[brand];
    if (!brandNode?.cars) return;

    textVariants(brand).forEach((brandKey) => {
      if (!index[brandKey]) index[brandKey] = Object.create(null);

      brandNode.cars.forEach((car) => {
        const colorMap = Object.create(null);

        if (car?.colorImages && typeof car.colorImages === "object") {
          Object.keys(car.colorImages).forEach((color) => {
            textVariants(color).forEach((colorKey) => {
              colorMap[colorKey] = car.colorImages[color];
            });
          });
        } else if (Array.isArray(car?.colors)) {
          car.colors.forEach((entry) => {
            if (!entry?.name || !entry?.image) return;
            textVariants(entry.name).forEach((colorKey) => {
              colorMap[colorKey] = entry.image;
            });
          });
        }

        const payload = {
          colorMap,
          modelImage: car?.image || null,
        };

        textVariants(car?.name).forEach((modelKey) => {
          index[brandKey][modelKey] = payload;
        });
      });
    });
  });

  return index;
}

export function getVehicleImageFromIndex(index, brand, model, color) {
  const brandKeys = textVariants(brand);
  const modelKeys = textVariants(model);
  const colorKeys = textVariants(color);

  for (const brandKey of brandKeys) {
    const brandNode = index?.[brandKey];
    if (!brandNode) continue;

    for (const modelKey of modelKeys) {
      const modelNode = brandNode?.[modelKey];
      if (!modelNode) continue;

      for (const colorKey of colorKeys) {
        if (colorKey && modelNode.colorMap?.[colorKey]) {
          return modelNode.colorMap[colorKey];
        }
      }

      if (modelNode.modelImage) return modelNode.modelImage;
    }
  }

  return null;
}
